import React, { useEffect, useState } from 'react';
import { portalService, PortalAppointment } from '../services/portalService';
import ConfirmModal from '../../../components/common/ConfirmModal';
import { APPOINTMENT_STATUS_BADGE } from '../../../domain/statuses';

const PortalAppointmentsPage: React.FC = () => {
    const [appointments, setAppointments] = useState<PortalAppointment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [cancellingId, setCancellingId] = useState<number | null>(null);
    const [cancelError, setCancelError] = useState<string | null>(null);
    const [pendingCancelId, setPendingCancelId] = useState<number | null>(null);

    const fetchAppointments = (status?: string) => {
        setLoading(true);
        const params: Record<string, string> = {};
        if (status) params.status = status;

        portalService
            .getAppointments(params)
            .then((data) => setAppointments(data.data))
            .catch((err) => setError(err.response?.data?.message || 'No pudimos cargar tus citas. Por favor, inténtalo de nuevo más tarde.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchAppointments();
    }, []);

    const handleFilterChange = (status: string) => {
        setStatusFilter(status);
        fetchAppointments(status || undefined);
    };

    const requestCancelAppointment = (id: number) => {
        setPendingCancelId(id);
    };

    const cancelCancelRequest = () => {
        setPendingCancelId(null);
    };

    const confirmCancelAppointment = async () => {
        if (pendingCancelId == null) return;
        const id = pendingCancelId;
        setCancellingId(id);
        setCancelError(null);
        setPendingCancelId(null);

        try {
            await portalService.cancelAppointment(id);
            fetchAppointments(statusFilter || undefined);
        } catch (err: any) {
            setCancelError(err.response?.data?.message || 'No pudimos cancelar la cita. Por favor, inténtalo de nuevo.');
        } finally {
            setCancellingId(null);
        }
    };

    const canCancel = (appt: PortalAppointment): boolean => {
        if (!appt.status) return false;
        const statusName = appt.status.name?.toLowerCase();
        return statusName !== 'cancelled' && statusName !== 'completed';
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    return (
        <div className="portal-appointments">
            <h2 className="mb-4">
                <i className="fas fa-calendar-alt me-2"></i>Mis Citas
            </h2>

            {/* Cancel error feedback */}
            {cancelError && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {cancelError}
                    <button type="button" className="btn-close" onClick={() => setCancelError(null)} aria-label="Cerrar"></button>
                </div>
            )}

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row align-items-center">
                        <div className="col-auto">
                            <label className="fw-bold me-2">Filtrar por estado:</label>
                        </div>
                        <div className="col-auto">
                            <select
                                className="form-select form-select-sm"
                                value={statusFilter}
                                onChange={(e) => handleFilterChange(e.target.value)}
                            >
                                <option value="">Todos</option>
                                <option value="pending">Pendiente</option>
                                <option value="confirmed">Confirmada</option>
                                <option value="completed">Completada</option>
                                <option value="cancelled">Cancelada</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Appointments List */}
            {appointments.length === 0 ? (
                <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    No tienes citas{statusFilter ? ' con ese estado' : ''}.
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead className="table-light">
                            <tr>
                                <th>Fecha</th>
                                <th>Hora</th>
                                <th>Doctor</th>
                                <th>Estado</th>
                                <th>Motivo</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {appointments.map((appt) => (
                                <tr key={appt.id}>
                                    <td>{appt.scheduled_date}</td>
                                    <td>{appt.scheduled_time}</td>
                                    <td>
                                        {appt.doctor
                                            ? `Dr. ${appt.doctor.first_name} ${appt.doctor.last_name}`
                                            : '—'}
                                    </td>
                                    <td>
                                        <span
                                            className={
                                                APPOINTMENT_STATUS_BADGE[
                                                    (appt.status as { code?: string } | null)?.code ?? ''
                                                ] || 'custom-badge status-grey'
                                            }
                                        >
                                            {appt.status?.name || '—'}
                                        </span>
                                    </td>
                                    <td>{appt.reason || '—'}</td>
                                    <td>
                                        {canCancel(appt) ? (
                                            <button
                                                className="btn btn-sm btn-danger"
                                                onClick={() => requestCancelAppointment(appt.id)}
                                                disabled={cancellingId === appt.id}
                                            >
                                                {cancellingId === appt.id ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1"></span>
                                                        Cancelando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-times me-1"></i>
                                                        Cancelar
                                                    </>
                                                )}
                                            </button>
                                        ) : appt.medical_consultation?.video_call_link ? (
                                            <a
                                                href={appt.medical_consultation.video_call_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-sm btn-primary"
                                            >
                                                <i className="fas fa-video me-1"></i>
                                                Unirse
                                            </a>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmModal
                show={pendingCancelId !== null}
                title="Cancelar cita"
                body="¿Está seguro que desea cancelar esta cita? Esta acción no se puede deshacer."
                confirmLabel="Cancelar cita"
                confirmVariant="danger"
                loading={cancellingId !== null}
                onConfirm={confirmCancelAppointment}
                onCancel={cancelCancelRequest}
            />
        </div>
    );
};

export default PortalAppointmentsPage;